import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http, { coordinatorHttp, responderHttp } from "../api/http";
import Settings from "../types/settings";
import Option from "../types/option";

interface SettingsState {
  hasSettings?: boolean;
  settings?: Settings;
  incidentTypes: Option[];
  jobTypes: Option[];
  status: "idle" | "loading" | "failed";
}

const initialState: SettingsState = {
  incidentTypes: [],
  jobTypes: [],
  status: "idle",
};

export const fetchSettings = createAsyncThunk<Settings>(
  "settings/fetch",
  async () => {
    const response = await http.get<Settings>("/settings", {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  },
);

export const saveSettings = createAsyncThunk<Settings, Settings>(
  "settings/save",
  async (vm: Settings) => {
    const response = await http.post<Settings>("/settings", vm, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const getIncidentTypes = createAsyncThunk<Option[]>(
  "/alerts/get-incident-types",
  async () => {
    const response = await responderHttp.get<Option[]>(`incident-types`);
    return response.data;
  },
);

export const getJobTypes = createAsyncThunk<Option[]>(
  "/alerts/get-gmd-types",
  async () => {
    const response = await coordinatorHttp.get<Option[]>(`/jobtype`);
    return response.data;
  },
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(saveSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.status = "idle";
        state.settings = action.payload;
      })
      .addCase(saveSettings.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getIncidentTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getIncidentTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.incidentTypes = action.payload;
      })
      .addCase(getIncidentTypes.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getJobTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobTypes = action.payload;
      })
      .addCase(getJobTypes.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export default settingsSlice.reducer;
